import { useQuery } from "@tanstack/react-query";
import { useApi } from "../../api/ApiProvider";
import { insuranceTransactionKeys } from "./insurance-transaction.keys";

/**
 * Searches prescriptions (Rx) by patient's last name for manual addition to an insurance bill.
 *
 * Used by: Add Rx spotlight search in insurance bill creation UI.
 */
export const useSearchRxByPatientName = (searchTerm: string, enabled = true) => {
  const { transaction } = useApi();

  return useQuery({
    queryKey: insuranceTransactionKeys.searchRxByPatient.forSearchTerm(searchTerm),
    queryFn: () => transaction.searchRxToAddInsuranceBill(searchTerm),
    enabled: enabled && searchTerm.length >= 3,
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });
};

/**
 * Loads all GKV bills grouped by payer for a given account.
 * No caching — always fetches fresh data on mount.
 */
export const useLoadGkvBills = (accountId: string, enabled = true) => {
  const { transaction } = useApi();

  return useQuery({
    queryKey: insuranceTransactionKeys.gkvBills.forAccount(accountId),
    queryFn: () => transaction.loadGkvBills(accountId),
    enabled: enabled && !!accountId,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
};

/**
 * Loads GKV bills for editing — optimized batch endpoint.
 * Fetches lean display data + calculated prices for all requested bills.
 * No caching — always fetches fresh data when the edit page opens.
 */
export const useLoadGkvBillsForEdit = (billIds: string[], enabled = true) => {
  const { transaction } = useApi();

  return useQuery({
    queryKey: insuranceTransactionKeys.gkvBillEdit.forBillIds(billIds),
    queryFn: () => transaction.loadGkvBillsForEdit(billIds),
    enabled: enabled && billIds.length > 0,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
};

/**
 * Batch validates Rxs by their IDs (lazy loader — called after loadGkvBills).
 * No caching — validation is always computed fresh.
 */
export const useValidateGkvBillRxs = (rxIds: string[], enabled = true) => {
  const { transaction } = useApi();

  return useQuery({
    queryKey: insuranceTransactionKeys.gkvValidation.forRxIds(rxIds),
    queryFn: ({ signal }) => transaction.validateGkvBillRxs({ rxIds }, { signal }),
    enabled: enabled && rxIds.length > 0,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
};

/**
 * Loads a single Rx by ID for adding to the direct billing list.
 * Includes price, billing centers, and validation.
 * No caching — each add is a one-shot fetch.
 */
export const useLoadSingleGkvBillRx = (rxId: string, enabled = true) => {
  const { transaction } = useApi();

  return useQuery({
    queryKey: insuranceTransactionKeys.gkvSingleRx.forRx(rxId),
    queryFn: () => transaction.loadSingleGkvBillRx(rxId),
    enabled: enabled && !!rxId,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });
};
